import type { ReactNode } from 'react';
import { Link } from 'react-router';
import styles from './ui.module.css';

/** Centred, gutter-aware content column. */
export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string | undefined;
}) {
  return <div className={[styles.container, className].filter(Boolean).join(' ')}>{children}</div>;
}

/**
 * `default` - Services and Company.
 * `contact` - Contact: slightly smaller title on a narrower measure.
 * `legal`   - Privacy, Cookies and Terms: smallest title, tighter band.
 */
type HeroVariant = 'default' | 'contact' | 'legal';

interface PageHeroProps {
  /** Small uppercase label above the title. */
  eyebrow: string;
  title: ReactNode;
  lead: ReactNode;
  /** Rendered below the lead. */
  children?: ReactNode;
  variant?: HeroVariant;
}

const cx = (...values: (string | false | undefined)[]) => values.filter(Boolean).join(' ');

/** The grid-paper hero band used by every page except Home. */
export function PageHero({ eyebrow, title, lead, children, variant = 'default' }: PageHeroProps) {
  const legal = variant === 'legal';

  return (
    <div className={styles.gridBand}>
      <div className={cx(styles.heroInner, legal && styles.heroInnerCompact)}>
        <p className={cx(styles.eyebrow, legal && styles.eyebrowCompact)}>
          <span className={styles.eyebrowDot} aria-hidden="true" />
          {eyebrow}
        </p>
        <h1
          className={cx(
            styles.heroTitle,
            variant === 'contact' && styles.heroTitleContact,
            legal && styles.heroTitleCompact,
          )}
        >
          {title}
        </h1>
        {lead}
        {children}
      </div>
    </div>
  );
}

/** Standard lead paragraph for a page hero. */
export function HeroLead({ children, narrow = false }: { children: ReactNode; narrow?: boolean }) {
  return <p className={cx(styles.heroLead, narrow && styles.heroLeadNarrow)}>{children}</p>;
}

/** The "Last updated …" line on a legal page. */
export function HeroMeta({ children }: { children: ReactNode }) {
  return <p className={styles.heroMeta}>{children}</p>;
}

/**
 * Indeterminate ring for a control that is waiting on the network.
 *
 * Purely decorative and hidden from assistive technology on purpose: a spinner
 * conveys "wait" to someone watching it and nothing at all to someone who is not.
 * Every caller pairs it with a text label and a live region, which are what
 * actually carry the state - see the submit button in Contact.tsx and
 * PhoneReveal.tsx.
 */
export function Spinner() {
  return <span className={styles.spinner} aria-hidden="true" />;
}

interface CtaBandProps {
  title: string;
  text: string;
  /** Button label; the arrow is part of the design's copy. */
  action?: string;
}

/** The closing "start a conversation" band shared by Services and Company. */
export function CtaBand({ title, text, action = 'Start a conversation →' }: CtaBandProps) {
  return (
    <section className={styles.ctaBand} aria-labelledby="cta-heading">
      <div className={styles.ctaInner}>
        <div>
          <h2 id="cta-heading" className={styles.ctaTitle}>
            {title}
          </h2>
          <p className={styles.ctaText}>{text}</p>
        </div>
        <Link to="/contact" className={styles.buttonPrimary}>
          {action}
        </Link>
      </div>
    </section>
  );
}
